import { useState, type ReactNode } from 'react';
import type { Tone } from '@pc/lib/tone';
import { Link, useRouter } from '@tanstack/react-router';
import type { operations } from '@pc/api/schema';

/**
 * The primitives every screen is built from.
 *
 * Deliberately unstyled at the call site: all appearance lives in
 * styles/app.css, so the console can be restyled without touching a screen.
 */

export function Card({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <section className="card">
      {title ? <h2 className="card-title">{title}</h2> : null}
      {children}
    </section>
  );
}

/** Definition list built from pairs, so screens do not hand-roll <dl> markup. */
export function Facts({ items }: { items: Array<[label: string, value: ReactNode]> }) {
  return (
    <dl className="facts">
      {items.map(([label, value]) => (
        <div key={label} className="fact">
          <dt>{label}</dt>
          <dd>{value ?? <Absent />}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A machine identifier — VMID, Discord snowflake, IPv4, VLAN tag, hash.
 * Set in mono because these get transcribed, compared and pasted into other
 * tools, where a lookalike character costs real time.
 */
export function Id({ children }: { children: ReactNode }) {
  return <span className="id">{children}</span>;
}

/**
 * An optional field the contract did not send. Distinct from an empty value —
 * "not provided" and "provided as empty" are different facts.
 */
export function Absent({ label = 'not provided' }: { label?: string }) {
  return <span className="absent">{label}</span>;
}

/**
 * `abandoned` is a tone of its own, not a shade of `bad`. A job in that state
 * was interrupted mid-run and may have left infrastructure half-built; giving
 * it the failure colour buries the one case that needs a human.
 */
// Re-exported so the many call sites that already import `Tone` alongside a
// component from this file keep working. It is *defined* in @pc/lib/tone
// because packages/lib/jobs.ts needs it and must not import console UI.
export type { Tone };

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className="badge" data-tone={tone}>
      {children}
    </span>
  );
}

/**
 * A boolean rendered as what it means, never as the literal word `true` or
 * `false`.
 *
 * `yes` and `no` are required rather than defaulted to the words "yes" and
 * "no". A default would be right often enough — "Changed: yes", "Created:
 * no" — to stop anyone thinking about the other case, and wrong exactly where
 * it matters: `active`/`inactive`, `found`/`not found` are not a yes and a
 * no, and a control that let them fall back to one would reintroduce the same
 * mistake `String(flag)` made, just one layer up.
 */
export function Flag({
  value,
  yes,
  no,
  tone,
}: {
  value: boolean;
  /** What it means when true. Never "true". */
  yes: string;
  /** What it means when false. Never "false". */
  no: string;
  /** Omit for plain text; pass a pair to render as a badge. */
  tone?: { yes: Tone; no: Tone };
}): ReactNode {
  const word = value ? yes : no;
  return tone ? <Badge tone={value ? tone.yes : tone.no}>{word}</Badge> : word;
}

export function Loading({ what }: { what: string }) {
  // `.loading` opts out of the notice styling every other `role="status"` gets
  // — see app.css. Announced, but not something to act on.
  return (
    <p role="status" className="loading">
      Loading {what}…
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

type CopyState = 'idle' | 'copied' | 'failed';

const COPY_LABEL: Record<CopyState, string> = {
  idle: 'copy',
  copied: 'copied',
  failed: 'copy blocked',
};

const COPIED_TITLE = 'Copied to the clipboard';
const COPY_BLOCKED_TITLE =
  'The browser did not allow copying — select the id and copy it by hand.';

const AUDIT_LINK_TITLE = 'Open in the audit trail';

/**
 * An identifier the operator has two unrelated reasons to want.
 *
 * The console produces exactly one kind of string like this: opaque, opaque on
 * purpose, and the direct handle on something that happened. A 6-hex opId ties
 * what someone saw here to the server log line and the audit record; a
 * 32-character job id names one five-minute teardown out of every job the
 * system has run. `listJobs` can find that job again by kind, subject or
 * target, but it cannot be filtered to a job id — so a search is a hunt through
 * rows for something the operator has to recognise, where the id simply opens
 * it. Both get pasted into a support conversation, and both get followed to the
 * screen that knows more.
 *
 * Those are two controls, not one control with a hidden mode. The opId used to
 * be a link whose plain click copied and silently cancelled the navigation, so
 * an operator who clicked it expecting the audit trail saw nothing happen at
 * all; the fix was to split them, and this is where that split lives now.
 * `destination` is what varies between the two — everything else, including
 * every way the clipboard can refuse, is the same problem twice.
 */
export function CopyableId({
  value,
  destination,
  destinationTitle,
  copyTitle,
}: {
  value: string;
  destination: Destination;
  destinationTitle: string;
  /** Only the resting title varies — it names what kind of id this is. */
  copyTitle: string;
}) {
  const [copy, setCopy] = useState<CopyState>('idle');

  function copyValue() {
    const clipboard = navigator.clipboard;
    // Absent entirely outside a secure context, and `writeText` can be refused
    // even inside one. Either way the id is on screen as selectable text, so
    // say the copy did not happen rather than leaving a button that appears to
    // have worked.
    if (!clipboard) {
      setCopy('failed');
      return;
    }

    void clipboard.writeText(value).then(
      () => {
        setCopy('copied');
        window.setTimeout(() => setCopy('idle'), 1200);
      },
      () => setCopy('failed'),
    );
  }

  const title = copy === 'idle' ? copyTitle : copy === 'copied' ? COPIED_TITLE : COPY_BLOCKED_TITLE;

  return (
    <span className="opid-pair">
      <RouteLink className="opid" destination={destination} title={destinationTitle}>
        {value}
      </RouteLink>

      <button
        type="button"
        className="opid-copy"
        data-state={copy}
        title={title}
        onClick={copyValue}
      >
        {COPY_LABEL[copy]}
      </button>
    </span>
  );
}

/**
 * Where following an id leads: a route this app registers, plus the search that
 * names the id on it.
 *
 * Always a `search` object, never a hand-built href — the router owns
 * serialisation, and it is configured (see `src/router.ts`) to leave search
 * values as the strings they arrived as. A snowflake put through the default
 * JSON parser comes back rounded, naming a different account without saying so.
 */
export interface Destination {
  to: string;
  search: Record<string, string>;
}

/**
 * A `<Link>` that survives having no router.
 *
 * `<Link>` reads router context unguarded and throws outright when there is
 * none, which is not hypothetical here: `ApiErrorNotice` is the one surface
 * that has to survive anything — including a failure raised before or outside
 * the router, where a crashing error notice would take the error itself down
 * with it — and `JobStatus` and the mutation-result panels are rendered from
 * components that are unit-tested without a router at all. `useRouter` is
 * declared as always returning a router but actually reads a context defaulting
 * to null, so the widened type below is describing what it does rather than
 * working around what it claims.
 *
 * Both branches are built from the same `destination`, so they cannot lead to
 * two different places. `Link` resolves `search` against the one route `to`
 * names, and the callers here are deliberately route-agnostic, so stating the
 * runtime shape and widening is more honest than making each of them pass a
 * pre-built link.
 */
function RouteLink({
  destination,
  title,
  className,
  children,
}: {
  destination: Destination;
  title: string;
  className?: string | undefined;
  children: ReactNode;
}) {
  const router: unknown = useRouter({ warn: false });

  if (!router) {
    // Same destination, without client-side routing. Only reached when there is
    // no router to route with.
    return (
      <a className={className} href={hrefFor(destination)} title={title}>
        {children}
      </a>
    );
  }

  return (
    <Link
      className={className}
      to={destination.to as never}
      search={destination.search as never}
      title={title}
    >
      {children}
    </Link>
  );
}

/** The router-less form of the same link, so both branches lead to one place. */
function hrefFor({ to, search }: Destination): string {
  const query = new URLSearchParams(search).toString();
  return query.length > 0 ? `${to}?${query}` : to;
}

/**
 * The correlation id for one operation: pasted into a support thread, or
 * followed into the audit record for the same operation.
 */
export function OpId({ opId }: { opId: string }) {
  return (
    <CopyableId
      value={opId}
      destination={{ to: '/audit', search: { opId } }}
      destinationTitle={AUDIT_LINK_TITLE}
      copyTitle="Copy this operation id"
    />
  );
}

/**
 * The receipt for a mutation that worked, where there is no result table to
 * put a row in.
 *
 * Every mutation's 200 now carries the `opId` its audit record was written
 * under, which makes a successful write as traceable as a failed one — so a
 * power action that previously left nothing behind but a changed badge can now
 * be followed into the trail. `role="status"` rather than an alert: this is
 * the good outcome, announced once, and it sits beside the controls it
 * describes rather than interrupting them.
 *
 * `children` is the past-tense phrase for what happened ("Started",
 * "Capability enabled"), so the line reads as a receipt rather than a label.
 *
 * `incomplete` is the other shape a 200 can turn out to have: some of these
 * responses report their own steps, and a step reported as not done means
 * `children`'s past tense is not entirely earned — a route that is "removed"
 * while the gateway still serves it, for one. Omitted or empty, this renders
 * exactly as it always has, so every caller that has never heard of this prop
 * stays byte-identical. Given a non-empty list, the good outcome above no
 * longer applies: this becomes the one receipt an operator must not scroll
 * past.
 */
export function OpReceipt({
  opId,
  incomplete,
  children,
}: {
  opId: string;
  /**
   * Steps the operation reported as not completed. Each entry is a plain
   * sentence naming what did not happen and what is therefore still true.
   */
  incomplete?: readonly string[];
  children: ReactNode;
}) {
  if (incomplete && incomplete.length > 0) {
    return (
      <div role="alert" className="op-receipt op-receipt-incomplete">
        {children} — but not completely. · <OpId opId={opId} />
        <ul>
          {incomplete.map((sentence) => (
            <li key={sentence}>{sentence}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <p role="status" className="op-receipt">
      {children} · <OpId opId={opId} />
    </p>
  );
}

/* ------------------------------------------------------------------ people */

/**
 * One human, as the directory answered for them.
 *
 * Taken off `getAccount` rather than restated: seventeen places in the contract
 * carry a person and every one of them carries this identical shape, so one can
 * stand for all of them — and a field added, renamed or dropped upstream lands
 * here as a compile error instead of as something this component quietly stops
 * rendering.
 */
export type PersonRecord =
  operations['getAccount']['responses'][200]['content']['application/json']['owner'];

export type PersonStatus = PersonRecord['status'];

/**
 * The shape the contract puts on every Discord id it will accept — the
 * `/accounts` route's own `discordID` path parameter declares this same
 * `^[1-9][0-9]{16,19}$`. A person's `discordID` is the one field guaranteed
 * present, but it can still arrive off a DB row with no such guarantee
 * attached, and an id of another shape names no account `/accounts` can look
 * up. A link that lands on a refusal is worse than the plain text it replaced.
 */
const DISCORD_ID_PATTERN = /^[1-9][0-9]{16,19}$/;

/**
 * What the directory's answer amounts to, per status — and why these are three
 * separate sentences rather than one.
 *
 * This is the same distinction `src/api/errors.ts` exists to protect, moved
 * down one level from the response to the field. `not-found` is the directory
 * answering: it holds nobody under this id, and it will say so again tomorrow.
 * `unavailable` is the directory never being reached, so every null beside it
 * means *unknown* and not *absent* — the person may be perfectly well known,
 * and a retry may well say so. Telling an operator mid-incident that a
 * container has no owner, when the truth is that LOCID is down, is exactly the
 * failure the error taxonomy was built to prevent.
 *
 * `found` is here too, because a found record is allowed to be sparse: the
 * contract says any name field may still be null. That is a person whose name
 * is not written down — not an absent person — so it gets wording that keeps
 * them a person.
 */
const DIRECTORY_NOTE: Record<PersonStatus, { label: string; explanation: string }> = {
  found: {
    label: 'name not recorded',
    explanation: 'The directory holds a record for this person, but no name on it.',
  },
  'not-found': {
    label: 'no directory record',
    explanation:
      'The directory answered and holds no record under this id. A fact about the id, not a transient failure — retrying will say the same thing.',
  },
  unavailable: {
    label: 'directory unavailable',
    explanation:
      'The directory could not be consulted, so who this is is unknown rather than absent. This is transient; a retry may well answer.',
  },
};

const DEFAULT_PERSON_LINK_TITLE = "Open this person's account";

/**
 * A person, wherever the console names one.
 *
 * The name leads. The console used to show an 18-digit snowflake in the place a
 * human's name belongs, in front of operators who know these people by name and
 * had to go and look each id up to find out whose container they were about to
 * stop.
 *
 * The snowflake stays, second. It is the only field the contract guarantees, it
 * is what gets pasted into Proxmox, pfSense and the Discord client, and it is
 * the handle the audit trail is searched by — so removing it in favour of a
 * name would trade one transcription problem for another.
 */
export function Person({
  person,
  fallbackDiscordID = null,
  linkTitle = DEFAULT_PERSON_LINK_TITLE,
  compact = false,
  showId = true,
}: {
  person: PersonRecord | null | undefined;
  /**
   * The id to fall back on when the API reported no person at all.
   *
   * `getLXC` carries both `owner` and a plain `ownerDiscordID` (and both
   * `creator` and `db.creatorDiscordID`), and the person half is independently
   * nullable. When it is null the bare id is all there is, and dropping it
   * would lose the container's only record of who owns it.
   */
  fallbackDiscordID?: string | null;
  /** Names whose account the link opens — "the owner's", "the creator's". */
  linkTitle?: string;
  /** Drops the job title, for table cells with no room for it. */
  compact?: boolean;
  /**
   * Drops the trailing snowflake link.
   *
   * For the one caller that renders this inside a link of its own — a table
   * with a Discord ID column of its own two cells to the right, where the id
   * here would be both the second copy on the row and a link inside a link.
   * Everywhere else the id is the way into the account and stays.
   */
  showId?: boolean;
}) {
  const discordID = person?.discordID ?? fallbackDiscordID;

  if (!person) {
    /*
     * No person object and no id either: an absent *field*, which is not one of
     * the three directory answers. None of the wording below applies, because
     * the directory was never asked about anybody.
     */
    if (discordID === null || discordID === undefined) return <Absent />;

    // An id with no directory answer attached. Say nothing about the directory
    // — nothing was reported about it — and render the one fact there is.
    return (
      <span className="person" data-status="unreported">
        {showId ? <PersonId discordID={discordID} linkTitle={linkTitle} /> : <Id>{discordID}</Id>}
      </span>
    );
  }

  // Deliberately not falling back to `cmpID`. A member number is an id, not a
  // name, and rendering one in the name's place makes "44" look like what
  // somebody is called — in the accounts table, two cells left of a CMP ID
  // column already showing the same 44. Every status has a note for the
  // nameless case, and saying "name not recorded" is the more honest answer
  // than a number that reads as a name.
  const name = firstNonBlank(person.displayName, person.username);
  const title = compact ? undefined : firstNonBlank(person.title);

  // A name is only the whole story when the directory actually answered. Under
  // the other two statuses the note rides along beside whatever name came
  // through, because the caveat is about the record, not about the name.
  const note =
    name !== undefined && person.status === 'found' ? undefined : DIRECTORY_NOTE[person.status];

  return (
    <span className="person" data-status={person.status}>
      {name !== undefined && <span className="person-name">{name}</span>}

      {/*
        * "Matthew Ray · Executive Director" tells an operator who they are
        * dealing with in a way the name alone does not — a member and a
        * director are different conversations. Null on every member record, so
        * it is additive rather than something to reserve space for.
        */}
      {title !== undefined && <span className="person-title">{title}</span>}

      {note && (
        <span className="person-note" data-status={person.status} title={note.explanation}>
          {note.label}
        </span>
      )}

      {showId && discordID !== null && discordID !== undefined && (
        <PersonId discordID={discordID} linkTitle={linkTitle} />
      )}
    </span>
  );
}

/**
 * The first candidate that is actually there.
 *
 * Used for the name — what the person calls themselves, then their LOCID login.
 * Both are independently nullable and a `found` record may carry neither, so
 * picking a name is a search down the list rather than a default; when the
 * search comes up empty the caller renders the status note instead.
 *
 * Blank strings count as missing, for the name and the job title alike: a name
 * rendered as nothing at all reads as a broken component, an empty title leaves
 * a separator pointing at nothing, and "provided as empty" is not a distinction
 * anyone can act on here.
 */
function firstNonBlank(...candidates: Array<string | null | undefined>): string | undefined {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed !== undefined && trimmed.length > 0) return trimmed;
  }
  return undefined;
}

/**
 * The snowflake, as the way into the account it names.
 *
 * Handed to the router as the string it arrived as, never through `Number()` —
 * every snowflake exceeds `Number.MAX_SAFE_INTEGER`, and a round trip through a
 * number silently points the link at a *different* account.
 */
function PersonId({ discordID, linkTitle }: { discordID: string; linkTitle: string }) {
  if (!DISCORD_ID_PATTERN.test(discordID)) return <Id>{discordID}</Id>;

  return (
    <RouteLink destination={{ to: '/accounts', search: { discordID } }} title={linkTitle}>
      <Id>{discordID}</Id>
    </RouteLink>
  );
}

/**
 * A field whose value could not be looked up, as against one that came back
 * empty. The sibling of `Absent`, and the difference between them is the whole
 * point: `Absent` asserts there is nothing there, this one asserts only that we
 * do not know. Under an `unavailable` person every null field is this, not that.
 */
export function Unknown({ label = 'unknown' }: { label?: string }) {
  return <span className="unknown">{label}</span>;
}
